import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import SaveIcon from "@material-ui/icons/Save";
import DeleteIcon from "@material-ui/icons/Delete";
import { MuiPickersUtilsProvider, KeyboardDateTimePicker } from "@material-ui/pickers";
import Modal from "@material-ui/core/Modal";
import CircularProgress from "@material-ui/core/CircularProgress";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";

import NativeSelect from "@meetyoo/components/src/components/Selects/NativeSelect";
import { getSelectValues, getKeyFromSelectedValue, getSelectedValueTranslated } from "../../../constants/passCodeUtils";

import LocalizedUtils, { moment } from "../../../utils/LocalizedUtils";

import { BILLING_LEDGER_ENTRIES_PATH } from "../../../constants/router";

import { requestLedgerEntry, updateLedgerEntry, deleteLedgerEntry } from "../../../actions/billing";

import { difference } from "../../../utils/objectMethods";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(3, 2),
    margin: theme.spacing(1, 0),
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  readOnlyPadding: {
    paddingTop: theme.spacing(1),
  },
  lineExpander: {
    flexGrow: 1,
  },
  lineBreak: {
    width: "100%",
  },
  title: {
    flex: "0 0 auto",
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(1),
  },
  inLine: {
    width: "100%",
    display: "inline-flex",
    marginBottom: theme.spacing(1),
  },
  spaceButtons: {
    flex: "1",
    width: "10px",
  },
  button: {
    margin: theme.spacing(0, 1),
    width: 100,
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  progress: {
    margin: theme.spacing(2),
  },
}));

const LedgerValues = { nettype: { 0: "Festnetz", 1: "Mobilfunk" } };

export const BillingLedgerEntryComponent = ({
  currentLedgerEntry,
  match,
  getLedgerEntry,
  translate,
  updateLedgerEntryHandler,
  deleteLedgerEntryHandler,
}) => {
  const [leOriginal, setLeOriginal] = useState({});
  const [leRestRep, setLeRestRep] = useState(leOriginal);
  const [updateRequest, setUpdateRequest] = useState(false);
  const [deleteRequest, setDeleteRequest] = useState(false);

  const id = Number(match.params.id);
  useEffect(() => {
    if (currentLedgerEntry !== null && currentLedgerEntry.id === Number(id)) {
      // alles geladen
      setLeOriginal(currentLedgerEntry);
      setLeRestRep(currentLedgerEntry);
    } else {
      getLedgerEntry(id);
    }
    setUpdateRequest(false);
  }, [currentLedgerEntry, getLedgerEntry, id]);

  const handleTextChange = name => event => {
    setLeRestRep({ ...leRestRep, [name]: event.target.value });
  };

  const handleSelectChange = name => event => {
    setLeRestRep({
      ...leRestRep,
      [name]: getKeyFromSelectedValue(LedgerValues[name], event.target.value),
    });
  };

  const handleDateChange = (name, date) => {
    setLeRestRep({ ...leRestRep, [name]: moment.utc(date).valueOf() });
  };

  const customStyle = theme => ({
    formControl: {
      marginTop: "16px",
      flexGrow: 1,
    },
  });
  const classes = useStyles();
  return deleteRequest || updateRequest ? (
    <Redirect to={BILLING_LEDGER_ENTRIES_PATH} />
  ) : (
    [
      <Modal
        key="update-modal"
        open={updateRequest}
        className={classes.modal}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={updateRequest}>
          <CircularProgress className={classes.progress} style={{ userSelect: "none" }} />
        </Fade>
      </Modal>,
      <Paper key={`billing-data-${leRestRep.id || ""}-${updateRequest}`} className={classes.paper}>
        <div className={classes.inLine}>
          <div className={classes.title}>
            <Typography variant="h6" id="tableTitle">
              {translate.billingData}
            </Typography>
          </div>
          <div className={classes.spaceButtons} />
          <Button
            key="ledger_entries_btn_save"
            variant="contained"
            size="small"
            className={classes.button}
            onClick={() => {
              updateLedgerEntryHandler(id, difference(leRestRep, leOriginal));
              setUpdateRequest(true);
            }}
          >
            <SaveIcon className={clsx(classes.leftIcon, classes.iconSmall)} />
            {translate.saveButton}
          </Button>
          <Button
            key="ledger_entries_btn_del"
            variant="contained"
            color="secondary"
            className={classes.button}
            onClick={() => {
              if (window.confirm("Are you sure to delete this entry?")) {
                deleteLedgerEntryHandler(id);
                setDeleteRequest(true);
              }
            }}
          >
            {translate.deleteButton}
            <DeleteIcon className={classes.rightIcon} />
          </Button>
        </div>
        <form key={leRestRep.id} className={classes.container} noValidate autoComplete="off">
          <TextField
            id="ledger-entry-id"
            label="Id"
            className={clsx(classes.textField, classes.lineExpander)}
            style={{ "--background-color": "#ffffff" }}
            defaultValue={leRestRep.id || ""}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            id="ledger-entry-billing-group"
            label={translate.billingGroup}
            className={clsx(classes.textField, classes.lineExpander, classes.readOnlyPadding)}
            value={leRestRep.billingGroup || ""}
            margin="normal"
            onChange={handleTextChange("billingGroup")}
          />
          <TextField
            id="ledger-entry-customer_billing_reference"
            label={translate.customerBillingReference}
            className={clsx(classes.textField, classes.lineExpander, classes.readOnlyPadding)}
            value={leRestRep.customerBillingReference || ""}
            margin="normal"
            onChange={handleTextChange("customerBillingReference")}
          />
          <div className={classes.lineBreak} />
          <MuiPickersUtilsProvider libInstance={moment} utils={LocalizedUtils}>
            <KeyboardDateTimePicker
              id="ledger-entry-starting-time"
              label={translate.billingLedgerParticipantStartingTime}
              className={clsx(classes.textField, classes.lineExpander)}
              ampm={false}
              format="DD.MM.YYYY HH:mm:ss"
              margin="normal"
              value={new Date(leRestRep.startingTime) || ""}
              onChange={date => handleDateChange("startingTime", date)}
            />
            <KeyboardDateTimePicker
              id="ledger-entry-ending-time"
              label={translate.billingLedgerParticipantEndingTime}
              className={clsx(classes.textField, classes.lineExpander)}
              ampm={false}
              format="DD.MM.YYYY HH:mm:ss"
              margin="normal"
              value={new Date(leRestRep.endingTime) || ""}
              onChange={date => handleDateChange("endingTime", date)}
            />
          </MuiPickersUtilsProvider>
          <div className={classes.lineBreak} />
          <TextField
            id="ledger-entry-quantity"
            label={translate.quantity}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.quantity || ""}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            id="ledger-entry-quantity-adjusted"
            label={translate.quantityAdjust}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.quantityAdjusted || ""}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <div className={classes.lineBreak} />
          <TextField
            id="ledger-entry-stat"
            label="Status"
            className={clsx(classes.textField, classes.lineExpander)}
            defaultValue={leRestRep.stat}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            id="ledger-entry-error-code"
            label={translate.errorCodeLedgerEntry}
            className={clsx(classes.textField, classes.lineExpander)}
            defaultValue={leRestRep.errorCode}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            id="ledger-entry-call-typ"
            label="Typ"
            className={clsx(classes.textField, classes.lineExpander)}
            defaultValue={leRestRep.typ}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
        </form>
      </Paper>,
      <Paper key={`personal-data-${leRestRep.id || ""}`} className={classes.paper}>
        <div className={classes.title}>
          <Typography variant="h6" id="tableTitle">
            {translate.personalData}
          </Typography>
        </div>
        <form key={leRestRep.id} className={classes.container} noValidate autoComplete="off">
          <TextField
            id="ledger-entry-name"
            label={translate.name}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.name || ""}
            margin="normal"
            onChange={handleTextChange("name")}
          />
          <TextField
            id="ledger-entry-user-name"
            label={translate.participantName}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.userName || ""}
            margin="normal"
            onChange={handleTextChange("userName")}
          />
          <TextField
            id="ledger-entry-request-from"
            label={translate.requestFrom}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.requestFrom || ""}
            margin="normal"
            onChange={handleTextChange("requestFrom")}
          />
          <div className={classes.lineBreak} />
          <TextField
            id="ledger-entry-e-mail"
            label="eMail"
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.eMail || ""}
            margin="normal"
            onChange={handleTextChange("eMail")}
          />
          <TextField
            id="ledger-entry-country"
            label={translate.country}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.country || ""}
            margin="normal"
            onChange={handleTextChange("country")}
          />
        </form>
      </Paper>,
      <Paper key={`product-data-${leRestRep.id || ""}`} className={classes.paper}>
        <div className={classes.title}>
          <Typography variant="h6" id="tableTitle">
            {translate.productData}
          </Typography>
        </div>
        <form key={leRestRep.id} className={classes.container} noValidate autoComplete="off">
          <TextField
            id="ledger-entry-product-no"
            label={translate.productNumber}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.productNo || ""}
            margin="normal"
            onChange={handleTextChange("productNo")}
          />
          <TextField
            id="ledger-entry-contract-no"
            label={translate.contractNumber}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.contractNo || ""}
            margin="normal"
            onChange={handleTextChange("contractNo")}
          />
          <TextField
            id="ledger-entry-evn-dial-out-structure"
            label={translate.evnDialOutStructure}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.evnDialOutStructure || ""}
            margin="normal"
            onChange={handleTextChange("evnDialOutStructure")}
          />
          <div className={classes.lineBreak} />
          <div className={classes.lineBreak} />
          <TextField
            id="ledger-entry-conference-id"
            label={translate.conferenceId}
            className={clsx(classes.textField, classes.lineExpander)}
            defaultValue={leRestRep.conferenceId}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            id="ledger-entry-room-id"
            label={translate.passcodeConferenceRoomId}
            className={clsx(classes.textField, classes.lineExpander)}
            defaultValue={leRestRep.roomId}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            id="ledger-entry-call-detail-id"
            label={translate.callDetailId}
            className={clsx(classes.textField, classes.lineExpander)}
            defaultValue={leRestRep.callDetailId}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <div className={classes.lineBreak} />
          <TextField
            id="ledger-entry-pool-conf-id"
            label={translate.poolConfId}
            className={clsx(classes.textField, classes.lineExpander)}
            defaultValue={leRestRep.poolConfId}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            id="ledger-entry-pool-part-id"
            label={translate.poolPartId}
            className={clsx(classes.textField, classes.lineExpander)}
            defaultValue={leRestRep.poolPartId}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            id="ledger-entry-call-product"
            label={translate.product}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.product || ""}
            margin="normal"
            onChange={handleTextChange("product")}
          />
          <div className={classes.lineBreak} />
          <TextField
            id="ledger-entry-A-Phone-No"
            label={translate.aPhoneNumber}
            className={clsx(classes.textField, classes.lineExpander)}
            defaultValue={leRestRep.aPhoneNo}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            id="ledger-entry-B-Phone-No"
            label={translate.bPhoneNumber}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.bPhoneNo || ""}
            margin="normal"
            onChange={handleTextChange("bPhoneNo")}
          />
          <NativeSelect
            customStyle={customStyle}
            value={translate.netType}
            selectValues={getSelectValues(LedgerValues, "nettype")}
            defaultSelect={getSelectedValueTranslated(leRestRep.nettype, LedgerValues, "nettype") || "Festnetz"}
            onChange={handleSelectChange("nettype")}
          />
          <div className={classes.lineBreak} />
          <TextField
            id="ledger-entry-billingPoint"
            label={translate.billingPoint}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.billingPoint || ""}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            id="ledger-entry-billingTime"
            label={translate.billingTime}
            className={clsx(classes.textField, classes.lineExpander)}
            value={leRestRep.billingTime || ""}
            margin="normal"
            variant="filled"
            InputProps={{
              readOnly: true,
            }}
          />
          <div className={classes.lineBreak} />
        </form>
      </Paper>,
    ]
  );
};

BillingLedgerEntryComponent.defaultProps = {
  currentLedgerEntry: {},
};

BillingLedgerEntryComponent.propTypes = {
  currentLedgerEntry: PropTypes.object,
  match: PropTypes.object.isRequired,
  translate: PropTypes.object.isRequired,
  getLedgerEntry: PropTypes.func.isRequired,
  updateLedgerEntryHandler: PropTypes.func.isRequired,
  deleteLedgerEntryHandler: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  currentLedgerEntry: state.billing.currentLedgerEntry,
  translate: state.ui.translate,
});

const mapDispatchToProps = dispatch => ({
  getLedgerEntry: id => dispatch(requestLedgerEntry(id)),
  updateLedgerEntryHandler: (id, ledgerEntry) => dispatch(updateLedgerEntry(id, ledgerEntry)),
  deleteLedgerEntryHandler: id => dispatch(deleteLedgerEntry(id)),
});

const BillingLedgerEntry = connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingLedgerEntryComponent);

export default BillingLedgerEntry;
